import { createSelector } from '@reduxjs/toolkit';

export const selectIsLoggedIn = (state) => state?.userProfile?.isLogin;
export const selectFavorites = (state) => state?.userProfile?.favorites;
export const selectPendingSaves = (state) => state?.userProfile?.pendingSaves;
export const selectIsProfileFavoritesLoaded = (state) => state?.userProfile?.isProfileFavoritesLoaded;
export const selectUserId = (state) => state?.userProfile?.userId;
export const selectEmail = (state) => state?.userProfile?.email;
export const selectName = (state) => state?.userProfile?.name;
export const selectPhone = (state) => state?.userProfile?.phone;
export const selectZip = (state) => state?.userProfile?.zip;
export const selectlivesOutside = (state) => state?.userProfile?.livesOutside;

export const selectUserLocationCity = (state) => state?.userProfile?.userLocation?.userCity;
export const selectUserLocationCountry = (state) => state?.userProfile?.userLocation?.userCountry;
export const selectUserLocationMarketId = (state) => state?.userProfile?.userLocation?.userMarketId;
export const selectUserLocationState = (state) => state?.userProfile?.userLocation?.userState;
export const selectUserLocationZipCode = (state) => state?.userProfile?.userLocation?.userZipCode;
export const selectIsAuthModalOpen = (state) => state?.modal?.isAuthModalOpen;

/**
 * @public
 * selectGoogleOneTapLibraryLoadingStatus: returns the loading status of the Google One Tap Library.
 * @param {Object} state of the Redux store.
 *
 * @returns the loading status of the Google One Tap Library.
 */
export const selectGoogleOneTapLibraryLoadingStatus = (state) =>
  state?.authentication?.googleOneTapLibraryLoadingStatus;

/** this selector is intended to expose useful properties when the user use the save to favorites feature
 * but it is not logged in.
 */
export const selectUserIdAndPendingSaves = createSelector(selectUserId, selectPendingSaves, (userId, pendingSaves) => ({
  userId,
  pendingSaves,
}));

/* It is using the `createSelector` function from the `@reduxjs/toolkit`
library to create a memoized selector. It takes in multiple input selectors
(`selectUserLocationCity`, `selectUserLocationCountry`, `selectUserLocationMarketId`,
`selectUserLocationState`, `selectUserLocationZipCode`) and a result function. */
export const selectUserLocation = createSelector(
  selectUserLocationCity,
  selectUserLocationCountry,
  selectUserLocationMarketId,
  selectUserLocationState,
  selectUserLocationZipCode,
  (userLocationCity, userLocationCountry, userLocationMarketId, userLocationState, userLocationZipCode) => ({
    userLocationCity,
    userLocationCountry,
    userLocationMarketId,
    userLocationState,
    userLocationZipCode,
  })
);

/** this selector is intended to expose all the properties that should be included as common tracking properties.
 * the idea behind this is tha here should be placed the selectors/props that are usually used in tracking events.
 * then the consumers will filter and take the props they need, this will just serve the information.
 */
export const selectUserProfileTrackingCommonProperties = createSelector(
  selectUserId,
  selectEmail,
  selectName,
  selectPhone,
  selectZip,
  selectlivesOutside,
  selectFavorites,
  selectIsLoggedIn,
  (
    userId,
    email,
    name,
    phone,
    zip,
    livesOutside,
    favorites,
    isLoggedIn
  ) => ({
    userId,
    email,
    name,
    phone,
    zip,
    livesOutside,
    favorites,
    isLoggedIn,
  })
);

/**
 * Selector to retrieve the user's prefill form information from the Redux state.
 *
 * Combines the user's email, name, phone, zip code, and whether they live outside a certain area
 * into a single object.
 *
 * @function
 * @param {Object} state - The Redux state.
 * @returns {Object} An object containing the user's basic information:
 *   @property {string} email - The user's email address.
 *   @property {string} name - The user's name.
 *   @property {string} phone - The user's phone number.
 *   @property {string} zip - The user's zip code.
 *   @property {boolean} livesOutside - Whether the user lives outside the specified area.
 */
export const getUserPrefillFormData = createSelector(
  selectEmail,
  selectName,
  selectPhone,
  selectZip,
  selectlivesOutside,
  (email, name, phone, zip, livesOutside) => ({
    email,
    name,
    phone,
    zip,
    livesOutside,
  })
);